import { PondCard } from './PondCard';
import { Waves } from 'lucide-react';
import { toNumber } from '@/lib/number';
import type { PondOverview, AlertThresholds } from '@/types/api';

interface PondGridProps {
  ponds: PondOverview[];
  alertThresholds?: AlertThresholds | null;
  onPondClick: (pond: PondOverview) => void;
}

export function PondGrid({ ponds, alertThresholds, onPondClick }: PondGridProps) {
  if (ponds.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4 bg-gray-50/50 rounded-2xl border-2 border-dashed border-gray-200">
        <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
          <Waves className="h-8 w-8 text-gray-400" />
        </div>
        <p className="text-gray-600 font-medium">No ponds found</p>
        <p className="text-sm text-gray-500 mt-1">Create your first pond to get started</p>
      </div>
    );
  }

  // Sort: active ponds first (by alert count desc), then by name
  const sortedPonds = [...ponds].sort((a, b) => {
    // Active ponds first
    const aActive = a.status === 'active';
    const bActive = b.status === 'active';
    if (aActive && !bActive) return -1;
    if (!aActive && bActive) return 1;

    // Within active, sort by alert count (most alerts first)
    if (aActive && bActive) {
      const aAlerts = toNumber(a.alert_count, 0);
      const bAlerts = toNumber(b.alert_count, 0);
      if (aAlerts !== bAlerts) {
        return bAlerts - aAlerts;
      }
    }

    // Then alphabetically
    return a.name.localeCompare(b.name);
  });

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {sortedPonds.map((pond) => (
        <div
          key={pond.id}
          className="focus-within:ring-2 focus-within:ring-primary-500 focus-within:ring-offset-2 rounded-xl"
        >
          <PondCard pond={pond} alertThresholds={alertThresholds} onClick={() => onPondClick(pond)} />
        </div>
      ))}
    </div>
  );
}
